import { __decorate, __extends } from "tslib";
import { serializeAsVector3, serialize, serializeAsMeshReference } from "../Misc/decorators.js";
import { Logger } from "../Misc/logger.js";
import { Vector2, Vector3, Matrix } from "../Maths/math.vector.js";
import { VertexBuffer } from "../Buffers/buffer.js";
import { AbstractMesh } from "../Meshes/abstractMesh.js";
import { Material } from "../Materials/material.js";
import { MaterialHelper } from "../Materials/materialHelper.js";
import { StandardMaterial } from "../Materials/standardMaterial.js";
import { Texture } from "../Materials/Textures/texture.js";
import { RenderTargetTexture } from "../Materials/Textures/renderTargetTexture.js";
import { PostProcess } from "./postProcess.js";

import { CreatePlane } from "../Meshes/Builders/planeBuilder.js";
import "../Shaders/depth.vertex.js";
import "../Shaders/volumetricLightScattering.fragment.js";
import "../Shaders/volumetricLightScatteringPass.vertex.js";
import "../Shaders/volumetricLightScatteringPass.fragment.js";
import { Color4, Color3 } from "../Maths/math.color.js";
import { Viewport } from "../Maths/math.viewport.js";
import { RegisterClass } from "../Misc/typeStore.js";
/**
 *  Inspired by https://developer.nvidia.com/gpugems/gpugems3/part-ii-light-and-shadows/chapter-13-volumetric-light-scattering-post-process
 */
var VolumetricLightScatteringPostProcess = /** @class */ (function (_super) {
    __extends(VolumetricLightScatteringPostProcess, _super);
    /**
     * @constructor
     * @param name The post-process name
     * @param ratio The size of the post-process and/or internal pass (0.5 means that your postprocess will have a width = canvas.width 0.5 and a height = canvas.height 0.5)
     * @param camera The camera that the post-process will be attached to
     * @param mesh The mesh used to create the light scattering
     * @param samples The post-process quality, default 100
     * @param samplingMode The post-process filtering mode
     * @param engine The babylon engine
     * @param reusable If the post-process is reusable
     * @param scene The constructor needs a scene reference to initialize internal components. If "camera" is null a "scene" must be provided
     */
    function VolumetricLightScatteringPostProcess(name, ratio, camera, mesh, samples, samplingMode, engine, reusable, scene) {
        if (samples === void 0) { samples = 100; }
        if (samplingMode === void 0) { samplingMode = Texture.BILINEAR_SAMPLINGMODE; }
        var _this = this;
        var _a;
        _this = _super.call(this, name, "volumetricLightScattering", ["decay", "exposure", "weight", "meshPositionOnScreen", "density"], ["lightScatteringSampler"], ratio.postProcessRatio || ratio, camera, samplingMode, engine, reusable, "#define NUM_SAMPLES " + samples) || this;
        _this._screenCoordinates = Vector2.Zero();
        /**
         * Custom position of the mesh. Used if "useCustomMeshPosition" is set to "true"
         */
        _this.customMeshPosition = Vector3.Zero();
        /**
         * Set if the post-process should use a custom position for the light source (true) or the internal mesh position (false)
         */
        _this.useCustomMeshPosition = false;
        /**
         * If the post-process should inverse the light scattering direction
         */
        _this.invert = true;
        /**
         * Array containing the excluded meshes not rendered in the internal pass
         */
        _this.excludedMeshes = new Array();
        /**
         * Controls the overall intensity of the post-process
         */
        _this.exposure = 0.3;
        /**
         * Dissipates each sample's contribution in range [0, 1]
         */
        _this.decay = 0.96815;
        /**
         * Controls the overall intensity of each sample
         */
        _this.weight = 0.58767;
        /**
         * Controls the density of each sample
         */
        _this.density = 0.926;
        scene = (_a = camera === null || camera === void 0 ? void 0 : camera.getScene()) !== null && _a !== void 0 ? _a : scene; // parameter "scene" can be null.
        engine = scene.getEngine();
        _this._viewPort = new Viewport(0, 0, 1, 1).toGlobal(engine.getRenderWidth(), engine.getRenderHeight());
        // Configure mesh
        _this.mesh = mesh !== null && mesh !== void 0 ? mesh : VolumetricLightScatteringPostProcess.CreateDefaultMesh("VolumetricLightScatteringMesh", scene);
        // Configure
        _this._createPass(scene, ratio.passRatio || ratio);
        _this.onActivate = function (camera) {
            if (!_this.isSupported) {
                _this.dispose(camera);
            }
            _this.onActivate = null;
        };
        _this.onApplyObservable.add(function (effect) {
            _this._updateMeshScreenCoordinates(scene);
            effect.setTexture("lightScatteringSampler", _this._volumetricLightScatteringRTT);
            effect.setFloat("exposure", _this.exposure);
            effect.setFloat("decay", _this.decay);
            effect.setFloat("weight", _this.weight);
            effect.setFloat("density", _this.density);
            effect.setVector2("meshPositionOnScreen", _this._screenCoordinates);
        });
        return _this;
    }
    Object.defineProperty(VolumetricLightScatteringPostProcess.prototype, "useDiffuseColor", {
        /**
         * @hidden
         * VolumetricLightScatteringPostProcess.useDiffuseColor is no longer used, use the mesh material directly instead
         */
        get: function () {
            Logger.Warn("VolumetricLightScatteringPostProcess.useDiffuseColor is no longer used, use the mesh material directly instead");
            return false;
        },
        set: function (useDiffuseColor) {
            Logger.Warn("VolumetricLightScatteringPostProcess.useDiffuseColor is no longer used, use the mesh material directly instead");
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Returns the string "VolumetricLightScatteringPostProcess"
     * @returns "VolumetricLightScatteringPostProcess"
     */
    VolumetricLightScatteringPostProcess.prototype.getClassName = function () {
        return "VolumetricLightScatteringPostProcess";
    };
    VolumetricLightScatteringPostProcess.prototype._isReady = function (subMesh, useInstances) {
        var _a;
        var mesh = subMesh.getMesh();
        // Render this.mesh as default
        if (mesh === this.mesh && mesh.material) {
            return mesh.material.isReady(mesh);
        }
        var renderingMaterial = (_a = mesh._internalAbstractMeshDataInfo._materialForRenderPass) === null || _a === void 0 ? void 0 : _a[this._scene.getEngine().currentRenderPassId];
        if (renderingMaterial) {
            return renderingMaterial.isReadyForSubMesh(mesh, subMesh, useInstances);
        }
        var defines = [];
        var attribs = [VertexBuffer.PositionKind];
        var material = subMesh.getMaterial();
        // Alpha test
        if (material) {
            if (material.needAlphaTesting()) {
                defines.push("#define ALPHATEST");
            }
            if (mesh.isVerticesDataPresent(VertexBuffer.UVKind)) {
                attribs.push(VertexBuffer.UVKind);
                defines.push("#define UV1");
            }
            if (mesh.isVerticesDataPresent(VertexBuffer.UV2Kind)) {
                attribs.push(VertexBuffer.UV2Kind);
                defines.push("#define UV2");
            }
        }
        // Bones
        if (mesh.useBones && mesh.computeBonesUsingShaders) {
            attribs.push(VertexBuffer.MatricesIndicesKind);
            attribs.push(VertexBuffer.MatricesWeightsKind);
            defines.push("#define NUM_BONE_INFLUENCERS " + mesh.numBoneInfluencers);
            defines.push("#define BonesPerMesh " + (mesh.skeleton ? mesh.skeleton.bones.length + 1 : 0));
        }
        else {
            defines.push("#define NUM_BONE_INFLUENCERS 0");
        }
        // Instances
        if (useInstances) {
            defines.push("#define INSTANCES");
            MaterialHelper.PushAttributesForInstances(attribs);
            if (subMesh.getRenderingMesh().hasThinInstances) {
                defines.push("#define THIN_INSTANCES");
            }
        }
        // Get correct effect
        var drawWrapper = subMesh._getDrawWrapper(undefined, true);
        var cachedDefines = drawWrapper.defines;
        var join = defines.join("\n");
        if (cachedDefines !== join) {
            drawWrapper.setEffect(mesh
                .getScene()
                .getEngine()
                .createEffect("volumetricLightScatteringPass", attribs, ["world", "mBones", "viewProjection", "diffuseMatrix"], ["diffuseSampler"], join, undefined, undefined, undefined, { maxSimultaneousMorphTargets: mesh.numBoneInfluencers }), join);
        }
        return drawWrapper.effect.isReady();
    };
    /**
     * Sets the new light position for light scattering effect
     * @param position The new custom light position
     */
    VolumetricLightScatteringPostProcess.prototype.setCustomMeshPosition = function (position) {
        this.customMeshPosition = position;
    };
    /**
     * Returns the light position for light scattering effect
     * @return Vector3 The custom light position
     */
    VolumetricLightScatteringPostProcess.prototype.getCustomMeshPosition = function () {
        return this.customMeshPosition;
    };
    /**
     * Disposes the internal assets and detaches the post-process from the camera
     * @param camera
     */
    VolumetricLightScatteringPostProcess.prototype.dispose = function (camera) {
        var rttIndex = camera.getScene().customRenderTargets.indexOf(this._volumetricLightScatteringRTT);
        if (rttIndex !== -1) {
            camera.getScene().customRenderTargets.splice(rttIndex, 1);
        }
        this._volumetricLightScatteringRTT.dispose();
        _super.prototype.dispose.call(this, camera);
    };
    /**
     * Returns the render target texture used by the post-process
     * @return the render target texture used by the post-process
     */
    VolumetricLightScatteringPostProcess.prototype.getPass = function () {
        return this._volumetricLightScatteringRTT;
    };
    // Private methods
    VolumetricLightScatteringPostProcess.prototype._meshExcluded = function (mesh) {
        if (this.excludedMeshes.length > 0 && this.excludedMeshes.indexOf(mesh) !== -1) {
            return true;
        }
        return false;
    };
    VolumetricLightScatteringPostProcess.prototype._createPass = function (scene, ratio) {
        var _this = this;
        var engine = scene.getEngine();
        this._volumetricLightScatteringRTT = new RenderTargetTexture("volumetricLightScatteringMap", { width: engine.getRenderWidth() * ratio, height: engine.getRenderHeight() * ratio }, scene, false, true, 0);
        this._volumetricLightScatteringRTT.wrapU = Texture.CLAMP_ADDRESSMODE;
        this._volumetricLightScatteringRTT.wrapV = Texture.CLAMP_ADDRESSMODE;
        this._volumetricLightScatteringRTT.renderList = null;
        this._volumetricLightScatteringRTT.renderParticles = false;
        this._volumetricLightScatteringRTT.ignoreCameraViewport = true;
        var camera = this.getCamera();
        if (camera) {
            camera.customRenderTargets.push(this._volumetricLightScatteringRTT);
        }
        else {
            scene.customRenderTargets.push(this._volumetricLightScatteringRTT);
        }
        // Custom render function for submeshes
        var renderSubMesh = function (subMesh) {
            var _a;
            var renderingMesh = subMesh.getRenderingMesh();
            var effectiveMesh = subMesh.getEffectiveMesh();
            if (_this._meshExcluded(renderingMesh)) {
                return;
            }
            effectiveMesh._internalAbstractMeshDataInfo._isActiveIntermediate = false;
            var material = subMesh.getMaterial();
            if (!material) {
                return;
            }
            var scene = renderingMesh.getScene();
            var engine = scene.getEngine();
            // Culling
            engine.setState(material.backFaceCulling, undefined, undefined, undefined, material.cullBackFaces);
            // Managing instances
            var batch = renderingMesh._getInstancesRenderList(subMesh._id, !!subMesh.getReplacementMesh());
            if (batch.mustReturn) {
                return;
            }
            var hardwareInstancedRendering = engine.getCaps().instancedArrays && (batch.visibleInstances[subMesh._id] !== null || renderingMesh.hasThinInstances);
            if (_this._isReady(subMesh, hardwareInstancedRendering)) {
                var renderingMaterial = (_a = effectiveMesh._internalAbstractMeshDataInfo._materialForRenderPass) === null || _a === void 0 ? void 0 : _a[engine.currentRenderPassId];
                var drawWrapper = subMesh._getDrawWrapper();
                if (renderingMesh === _this.mesh && !drawWrapper) {
                    drawWrapper = material._getDrawWrapper();
                }
                if (!drawWrapper) {
                    return;
                }
                var effect_1 = drawWrapper.effect;
                engine.enableEffect(drawWrapper);
                if (!hardwareInstancedRendering) {
                    renderingMesh._bind(subMesh, effect_1, material.fillMode);
                }
                if (renderingMesh === _this.mesh) {
                    material.bind(effectiveMesh.getWorldMatrix(), renderingMesh);
                }
                else if (renderingMaterial) {
                    renderingMaterial.bindForSubMesh(effectiveMesh.getWorldMatrix(), effectiveMesh, subMesh);
                }
                else {
                    effect_1.setMatrix("viewProjection", scene.getTransformMatrix());
                    // Alpha test
                    if (material && material.needAlphaTesting()) {
                        var alphaTexture = material.getAlphaTestTexture();
                        effect_1.setTexture("diffuseSampler", alphaTexture);
                        if (alphaTexture) {
                            effect_1.setMatrix("diffuseMatrix", alphaTexture.getTextureMatrix());
                        }
                    }
                    // Bones
                    if (renderingMesh.useBones && renderingMesh.computeBonesUsingShaders && renderingMesh.skeleton) {
                        effect_1.setMatrices("mBones", renderingMesh.skeleton.getTransformMatrices(renderingMesh));
                    }
                }
                if (hardwareInstancedRendering && renderingMesh.hasThinInstances) {
                    effect_1.setMatrix("world", effectiveMesh.getWorldMatrix());
                }
                // Draw
                renderingMesh._processRendering(effectiveMesh, subMesh, effect_1, Material.TriangleFillMode, batch, hardwareInstancedRendering, function (isInstance, world) {
                    if (!isInstance) {
                        effect_1.setMatrix("world", world);
                    }
                });
            }
        };
        // Render target texture callbacks
        var savedSceneClearColor;
        var sceneClearColor = new Color4(0.0, 0.0, 0.0, 1.0);
        this._volumetricLightScatteringRTT.onBeforeRenderObservable.add(function () {
            savedSceneClearColor = scene.clearColor;
            scene.clearColor = sceneClearColor;
        });
        this._volumetricLightScatteringRTT.onAfterRenderObservable.add(function () {
            scene.clearColor = savedSceneClearColor;
        });
        this._volumetricLightScatteringRTT.customIsReadyFunction = function (mesh, refreshRate) {
            if (!mesh.isReady(false)) {
                return false;
            }
            if (refreshRate === 0 && mesh.subMeshes) {
                // full check: check that the effects are ready
                for (var i = 0; i < mesh.subMeshes.length; ++i) {
                    var subMesh = mesh.subMeshes[i];
                    var material = subMesh.getMaterial();
                    var renderingMesh = subMesh.getRenderingMesh();
                    if (!material) {
                        continue;
                    }
                    var batch = renderingMesh._getInstancesRenderList(subMesh._id, !!subMesh.getReplacementMesh());
                    var hardwareInstancedRendering = engine.getCaps().instancedArrays && (batch.visibleInstances[subMesh._id] !== null || renderingMesh.hasThinInstances);
                    if (!_this._isReady(subMesh, hardwareInstancedRendering)) {
                        return false;
                    }
                }
            }
            return true;
        };
        this._volumetricLightScatteringRTT.customRenderFunction = function (opaqueSubMeshes, alphaTestSubMeshes, transparentSubMeshes, depthOnlySubMeshes) {
            var engine = scene.getEngine();
            var index;
            if (depthOnlySubMeshes.length) {
                engine.setColorWrite(false);
                for (index = 0; index < depthOnlySubMeshes.length; index++) {
                    renderSubMesh(depthOnlySubMeshes.data[index]);
                }
                engine.setColorWrite(true);
            }
            for (index = 0; index < opaqueSubMeshes.length; index++) {
                renderSubMesh(opaqueSubMeshes.data[index]);
            }
            for (index = 0; index < alphaTestSubMeshes.length; index++) {
                renderSubMesh(alphaTestSubMeshes.data[index]);
            }
            if (transparentSubMeshes.length) {
                // Sort sub meshes
                for (index = 0; index < transparentSubMeshes.length; index++) {
                    var submesh = transparentSubMeshes.data[index];
                    var boundingInfo = submesh.getBoundingInfo();
                    if (boundingInfo && scene.activeCamera) {
                        submesh._alphaIndex = submesh.getMesh().alphaIndex;
                        submesh._distanceToCamera = boundingInfo.boundingSphere.centerWorld.subtract(scene.activeCamera.position).length();
                    }
                }
                var sortedArray = transparentSubMeshes.data.slice(0, transparentSubMeshes.length);
                sortedArray.sort(function (a, b) {
                    // Alpha index first
                    if (a._alphaIndex > b._alphaIndex) {
                        return 1;
                    }
                    if (a._alphaIndex < b._alphaIndex) {
                        return -1;
                    }
                    // Then distance to camera
                    if (a._distanceToCamera < b._distanceToCamera) {
                        return 1;
                    }
                    if (a._distanceToCamera > b._distanceToCamera) {
                        return -1;
                    }
                    return 0;
                });
                // Render sub meshes
                engine.setAlphaMode(2);
                for (index = 0; index < sortedArray.length; index++) {
                    renderSubMesh(sortedArray[index]);
                }
                engine.setAlphaMode(0);
            }
        };
    };
    VolumetricLightScatteringPostProcess.prototype._updateMeshScreenCoordinates = function (scene) {
        var transform = scene.getTransformMatrix();
        var meshPosition;
        if (this.useCustomMeshPosition) {
            meshPosition = this.customMeshPosition;
        }
        else if (this.attachedNode) {
            meshPosition = this.attachedNode.position;
        }
        else {
            meshPosition = this.mesh.parent ? this.mesh.getAbsolutePosition() : this.mesh.position;
        }
        var pos = Vector3.Project(meshPosition, Matrix.Identity(), transform, this._viewPort);
        this._screenCoordinates.x = pos.x / this._viewPort.width;
        this._screenCoordinates.y = pos.y / this._viewPort.height;
        if (this.invert) {
            this._screenCoordinates.y = 1.0 - this._screenCoordinates.y;
        }
    };
    // Static methods
    /**
     * Creates a default mesh for the Volumeric Light Scattering post-process
     * @param name The mesh name
     * @param scene The scene where to create the mesh
     * @return the default mesh
     */
    VolumetricLightScatteringPostProcess.CreateDefaultMesh = function (name, scene) {
        var mesh = CreatePlane(name, { size: 1 }, scene);
        mesh.billboardMode = AbstractMesh.BILLBOARDMODE_ALL;
        var material = new StandardMaterial(name + "Material", scene);
        material.emissiveColor = new Color3(1, 1, 1);
        mesh.material = material;
        return mesh;
    };
    __decorate([
        serializeAsVector3()
    ], VolumetricLightScatteringPostProcess.prototype, "customMeshPosition", void 0);
    __decorate([
        serialize()
    ], VolumetricLightScatteringPostProcess.prototype, "useCustomMeshPosition", void 0);
    __decorate([
        serialize()
    ], VolumetricLightScatteringPostProcess.prototype, "invert", void 0);
    __decorate([
        serializeAsMeshReference()
    ], VolumetricLightScatteringPostProcess.prototype, "mesh", void 0);
    __decorate([
        serialize()
    ], VolumetricLightScatteringPostProcess.prototype, "excludedMeshes", void 0);
    __decorate([
        serialize()
    ], VolumetricLightScatteringPostProcess.prototype, "exposure", void 0);
    __decorate([
        serialize()
    ], VolumetricLightScatteringPostProcess.prototype, "decay", void 0);
    __decorate([
        serialize()
    ], VolumetricLightScatteringPostProcess.prototype, "weight", void 0);
    __decorate([
        serialize()
    ], VolumetricLightScatteringPostProcess.prototype, "density", void 0);
    return VolumetricLightScatteringPostProcess;
}(PostProcess));
export { VolumetricLightScatteringPostProcess };
RegisterClass("BABYLON.VolumetricLightScatteringPostProcess", VolumetricLightScatteringPostProcess);
//# sourceMappingURL=volumetricLightScatteringPostProcess.js.map